import { Component, Injectable, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import {
    FilterData,
    IceGridReq,
    FilterValue,
    SelectedFilter,
    ParentColumnReference,
    DefaultFilter,
    TableRefrences,
    ChildColumnReference,
    Fields,
} from '../../models/ice-database.model';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { IceapiService } from 'src/app/services';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IceDatabaseService } from 'src/app/services/ice-database.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IceDetailsComponent } from '../ice-details/ice-details.component';

@Injectable()
export class MyCustomPaginatorIntl implements MatPaginatorIntl {
    changes = new Subject<void>();

    firstPageLabel = $localize`First page`;
    itemsPerPageLabel = $localize`Items per page:`;
    lastPageLabel = $localize`Last page`;
    nextPageLabel = 'Next page';
    previousPageLabel = 'Previous page';

    getRangeLabel(page: number, pageSize: number, length: number): string {
        if (length === 0) {
            return $localize`Page 1 of 1`;
        }
        const amountPages = Math.ceil(length / pageSize);
        return $localize`Page ${page + 1} of ${amountPages}`;
    }
}

@Component({
    selector: 'app-ice-grid',
    templateUrl: './ice-grid.component.html',
    styleUrls: ['./ice-grid.component.scss'],
    providers: [{ provide: MatPaginatorIntl, useClass: MyCustomPaginatorIntl }],
})
export class IceGridComponent implements OnInit, OnChanges, OnDestroy {
    @Input() selectedTable: string;
    @Input() defaultFilter: DefaultFilter;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    destroy$ = new Subject();

    dataSource = new MatTableDataSource<any>();
    displayedColumns: string[] = [];
    tableFields: Fields[] = [];
    totalData: number;
    pageSize: number = 20;
    searchText: string = '';
    searchText$ = new Subject<string>();
    tableRefrences: TableRefrences[];
    parentColumnReferences: ParentColumnReference[];
    parentColumns: string[];
    childColumnReferences: ChildColumnReference[];
    childColumns: string[];
    references?: string;
    tableFilterList: FilterData[];
    selectedTableFilter: FilterData;
    selectedFilterColumn: string;
    selectedFilters: SelectedFilter[] = [];
    selectdOptions: FilterValue[];
    activeFilters: string[] = [];
    overlayOrigin: any;
    filterOpen = false;
    overlayPositions = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 4,
        },
    ];

    constructor(
        private iceapiService: IceapiService,
        private ngxUiLoaderService: NgxUiLoaderService,
        private iceDatabaseService: IceDatabaseService,
        private ngbModal: NgbModal
    ) {}

    ngOnInit(): void {
        this.searchText$
            .pipe(debounceTime(800), distinctUntilChanged())
            .pipe(takeUntil(this.destroy$))
            .subscribe((searchText) => {
                this.getTableData(1, this.pageSize, searchText);
                if (this.paginator?.pageIndex) this.paginator.pageIndex = 0;
            });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (this.paginator?.pageIndex) this.paginator.pageIndex = 0;
        await this.setRefrence();

        this.selectedFilters = [];
        this.searchText = '';
        this.tableFilterList = await this.iceDatabaseService.getFilters(this.selectedTable);

        const nullDataInserted = new Set();
        this.parentColumnReferences.forEach((reference) => {
            const field = this.tableFields.find((fieldObj) => fieldObj.field === reference.fieldName);
            if (field && !field.isRequire && !nullDataInserted.has(reference.parentTableName)) {
                const filterItem = this.tableFilterList.find((item) => item.title === reference.parentTableName);
                if (filterItem) {
                    filterItem.data.unshift({ id: null, value: 'Null' });
                    nullDataInserted.add(reference.parentTableName);
                }
            }
        });

        if (this.defaultFilter) {
            const selected = this.parentColumnReferences.find((x) => x.fieldName == this.defaultFilter.fieldName);
            if (selected) {
                this.selectedFilters.push({
                    key: selected.childColumnName,
                    fieldName: selected.fieldName,
                    data: [this.tableFilterList.find((x) => x.title == selected.parentTableName).data.find((x) => x.id == this.defaultFilter.id)],
                });
            }
            this.defaultFilter = null;
        }
        this.activeFilters = this.selectedFilters.filter((x) => x.data.length).map((x) => x.fieldName);
        this.getTableData(1, this.pageSize);
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.setGridData();
    }

    setGridData(): void {
        if (this.paginator) {
            this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => {
                this.getTableData(this.paginator.pageIndex + 1, this.paginator.pageSize);
            });
        }
    }

    getTableData(pageNumber: number, pageSize: number, searchText?: string) {
        this.ngxUiLoaderService.start();
        const payload: IceGridReq = {
            tableName: this.selectedTable,
            pageNumber,
            pageSize,
            references: this.references,
            additionalWhere: this.createFilterQuery(),
            searchText: searchText ?? this.searchText,
        };

        this.iceapiService
            .getIceDatabaseGridData(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (res) => {
                    if (res.success) {
                        this.totalData = res.response.rowCount;
                        this.dataSource = new MatTableDataSource(res.response.data);
                    } else {
                        this.dataSource = new MatTableDataSource([]);
                        this.totalData = 0;
                    }
                    this.ngxUiLoaderService.stop();
                },
                (err) => {
                    this.ngxUiLoaderService.stop();
                }
            );
    }

    async setRefrence(): Promise<void> {
        this.tableRefrences = this.iceDatabaseService.tableRefrences.filter((x) => x.childTableName === this.selectedTable && x.childColumnName);
        this.childColumnReferences = this.iceDatabaseService.generateChildFields(this.selectedTable);
        this.childColumns = this.childColumnReferences.map((x) => x.fieldName);
        this.parentColumnReferences = this.iceDatabaseService.generateParentFields(this.tableRefrences);
        this.parentColumns = this.parentColumnReferences.map((x) => x.fieldName);
        this.references = this.tableRefrences.map((x) => x.childColumnName + '_' + x.parentTableName).join(',');
        this.tableFields = await this.iceDatabaseService.getTableFields(this.selectedTable, this.childColumns);
        this.displayedColumns = this.tableFields.map((x) => x.field);
    }

    createFilterQuery(): string {
        const filterQueries: string[] = [];

        this.selectedFilters.forEach((item) => {
            if (item.data.length) {
                const columnName = item.key;

                const selectedIds = item.data
                    .filter((selectedData) => selectedData.id !== null)
                    .map((selectedData) => `'${selectedData.id}'`)
                    .join(',');

                const nullFilter = item.data.some((selectedData) => selectedData.id === null);

                if (selectedIds && nullFilter) {
                    filterQueries.push(`(${columnName} IN (${selectedIds}) OR ${columnName} IS NULL)`);
                } else if (selectedIds) {
                    filterQueries.push(`${columnName} IN (${selectedIds})`);
                } else if (nullFilter) {
                    filterQueries.push(`${columnName} IS NULL`);
                }
            }
        });
        return filterQueries.join(' AND ');
    }

    searchTextChanged(): void {
        this.searchText$.next(this.searchText);
    }

    optionlistclick(fieldName: string, trigger: any): void {
        // Your existing logic
        const selected = this.parentColumnReferences.find((x) => x.fieldName == fieldName);
        this.selectedFilterColumn = selected.childColumnName;
        this.selectedTableFilter = this.tableFilterList.find((x) => x.title == selected.parentTableName);
        this.selectdOptions = this.selectedFilters.find((x) => x.fieldName == fieldName)?.data ?? [];

        // NEW → connect overlay origin
        this.overlayOrigin = trigger;

        // Open the popup
        this.filterOpen = true;
    }

    closeFilter() {
        this.filterOpen = false;
    }

    onSelectedOptions(values: FilterValue[]): void {
        let index = this.selectedFilters.findIndex((x) => x.key == this.selectedFilterColumn);
        const selected = this.parentColumnReferences.find((x) => x.childColumnName == this.selectedFilterColumn);
        if (index !== -1) {
            this.selectedFilters[index].data = values;
        } else {
            this.selectedFilters.push({ key: this.selectedFilterColumn, fieldName: selected.fieldName, data: values });
        }
        this.activeFilters = this.selectedFilters.filter((x) => x.data.length).map((x) => x.fieldName);
        this.selectedTableFilter = null;
        this.selectedFilterColumn = null;
        if (this.paginator?.pageIndex) this.paginator.pageIndex = 0;
        this.getTableData(1, this.pageSize);
    }

    onCancelOption(values): void {
        this.selectedTableFilter = null;
        this.selectedFilterColumn = null;
    }

    viewDetail(data: any, type: string): void {
        const dialogRef = this.ngbModal.open(IceDetailsComponent, {
            size: this.tableFields.length > 8 ? 'xl' : 'md',
            backdrop: 'static',
            keyboard: false,
        });
        dialogRef.componentInstance.type = type;
        dialogRef.componentInstance.tableName = this.selectedTable;
        dialogRef.componentInstance.rowData = data;
        dialogRef.componentInstance.column = this.tableFields.filter((field) => field.isComputed === false).slice(0, -1);
        dialogRef.componentInstance.childColumnReferences = this.childColumnReferences;
        dialogRef.componentInstance.parentColumnReferences = this.parentColumnReferences;
        dialogRef.componentInstance.tableFilterList = this.tableFilterList;

        dialogRef.result
            .then((res) => {
                if (res) {
                    if (['Edit', 'Add'].includes(res.type)) {
                        this.searchText = '';
                        this.selectedFilters = [];
                        this.searchText$.next('');
                    } else {
                        this.iceDatabaseService.selectedTableName.next(res.data);
                    }
                }
            })
            .catch((e) => {
                console.warn(e);
            });
    }

    deleteDetail(data: any): void {
        this.iceDatabaseService.deleteData(this.selectedTable, data, this.displayedColumns);
    }

    openParentDetails(columnName: string, data: any): void {
        this.iceDatabaseService.openParentDetails(columnName, data, this.parentColumnReferences);
    }

    openChildDetails(columnName: string, data: any): void {
        const child = this.childColumnReferences.find((x) => x.fieldName == columnName);
        const selectedData = {
            tableName: child.originalFieldName,
            fieldName: child.parentFieldName,
            id: data[this.displayedColumns[0]],
        };
        this.iceDatabaseService.selectedTableName.next(selectedData);
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
